import { useMemo, useState } from "react";
import { useApiUrl, useCustom, useCustomMutation, useList } from "@refinedev/core";
import {
  Loader2,
  Trash2,
  Plus,
  BookOpen,
  MessageSquare,
  Shield,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Radio,
  Pencil,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/date-picker";
import { endOfDayUtcIso, dateFromUtcDay } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DataTable, type BulkAction, type Column } from "@/components/tables";
import { notify } from '@/lib/notify';
import { getChannelPassName, type ChannelPass, type PricingTier } from "@/resources/channel-passes/types";
import { getCourseTitle, type Course } from "@/resources/courses/types";
import { getSubscriptionPlanName, type SubscriptionPlan } from "@/resources/subscription-plans/types";
import { getBookTitle, type Book } from "@/resources/books/types";
import { getLivestreamTitle, type Livestream } from "@/resources/livestreams/types";
import { AccessTargetType, OrderTargetType } from "@masters-club/types";

interface AccessGrant {
  id: number;
  targetType: string;
  targetId: number | null;
  targetName: string;
  sourceType: string;
  sourceId: number | null;
  sourceSubscriptionMemberId: number | null;
  sourceChannelPassId: number | null;
  orderId: number | null;
  status: string;
  startsAt: string;
  endsAt: string | null;
  createdAt: string;
}

interface UserAccessTableProps {
  userId: number;
  onGrantAccess?: () => void;
  grantModalOpen?: boolean;
  onGrantModalOpenChange?: (open: boolean) => void;
}

type GrantTargetType =
  | typeof OrderTargetType.COURSE
  | typeof OrderTargetType.CHANNEL_PASS
  | typeof OrderTargetType.SUBSCRIPTION_PLAN
  | typeof OrderTargetType.BOOK
  | typeof AccessTargetType.LIVESTREAM;

const TARGET_TYPES: { value: GrantTargetType; label: string; icon: typeof BookOpen }[] = [
  { value: OrderTargetType.COURSE, label: "Курс", icon: BookOpen },
  { value: OrderTargetType.CHANNEL_PASS, label: "Канал", icon: MessageSquare },
  { value: OrderTargetType.SUBSCRIPTION_PLAN, label: "Абонемент", icon: Shield },
  { value: OrderTargetType.BOOK, label: "Книга", icon: BookOpen },
  { value: AccessTargetType.LIVESTREAM, label: "Трансляция", icon: Radio },
];

const isGrantTargetType = (value: string): value is GrantTargetType =>
  TARGET_TYPES.some((type) => type.value === value);

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const translateSourceType = (sourceType: string) => {
  const translations: Record<string, string> = {
    SUBSCRIPTION: "Подписка",
    MANUAL: "Вручную",
    PROMOTION: "Акция",
    GIFT: "Подарок",
    ORDER: "Заказ",
  };
  return translations[sourceType] ?? sourceType;
};

const translateTargetType = (targetType: string) => {
  const translations: Record<string, string> = {
    COURSE: "Курс",
    EVENT: "Мероприятие",
    CHANNEL: "Канал",
    CONTENT: "Контент",
    MODULE: "Модуль",
    MODULE_ITEM: "Урок",
    CHANNEL_PASS: "Канал",
    SUBSCRIPTION_PLAN: "Абонемент",
    BOOK: "Книга",
    LIVESTREAM: "Трансляция",
  };
  return translations[targetType] ?? targetType;
};

const getSourceDetails = (grant: AccessGrant) => {
  const details = [
    grant.orderId ? `заказ #${grant.orderId}` : null,
    grant.sourceId ? `источник #${grant.sourceId}` : null,
    grant.sourceSubscriptionMemberId ? `участник подписки #${grant.sourceSubscriptionMemberId}` : null,
    grant.sourceChannelPassId ? `пропуск #${grant.sourceChannelPassId}` : null,
    `доступ #${grant.id}`,
  ].filter((detail): detail is string => detail !== null);

  return details.join(" · ");
};

const getTierName = (tier: PricingTier) => {
  const trs = tier.translations ?? {};
  const ordered = [trs.ru, trs.en, trs.uz, ...Object.values(trs)];
  for (const tr of ordered) {
    if (tr?.name) { return tr.name; }
  }
  return `#${tier.id}`;
};

const getTierPriceLabel = (tier: PricingTier) => {
  const amount = tier.prices[tier.baseCurrency];
  if (amount !== undefined) {
    return `${amount.toLocaleString("ru-RU")} ${tier.baseCurrency}`;
  }

  const [currency, fallbackAmount] = Object.entries(tier.prices)[0] ?? [];
  if (!currency || fallbackAmount === undefined) {
    return "Цена не указана";
  }

  return `${fallbackAmount.toLocaleString("ru-RU")} ${currency}`;
};

export function UserAccessTable({
  userId,
  onGrantAccess,
  grantModalOpen: externalModalOpen,
  onGrantModalOpenChange,
}: UserAccessTableProps) {
  const [internalModalOpen, setInternalModalOpen] = useState(false);
  const [deleteItems, setDeleteItems] = useState<AccessGrant[]>([]);
  const [targetType, setTargetType] = useState<GrantTargetType | "">("");
  const [targetId, setTargetId] = useState("");
  const [pricingTierId, setPricingTierId] = useState("");
  const [durationDays, setDurationDays] = useState("");
  const apiUrl = useApiUrl();
  const targetSupportsPricingTier = targetType !== AccessTargetType.LIVESTREAM;

  const grantModalOpen = externalModalOpen ?? internalModalOpen;
  const setGrantModalOpen = (open: boolean) => {
    if (onGrantModalOpenChange) {
      onGrantModalOpenChange(open);
      return;
    }
    setInternalModalOpen(open);
  };

  const grantsQuery = useCustom<AccessGrant[]>({
    url: `/users/${userId}/access-grants`,
    method: "get",
  });

  const coursesQuery = useList<Course>({
    resource: "courses",
    pagination: { pageSize: 100, mode: "server" },
  });

  const channelPassesQuery = useList<ChannelPass>({
    resource: "channel-passes",
    pagination: { pageSize: 100, mode: "server" },
  });

  const subscriptionPlansQuery = useList<SubscriptionPlan>({
    resource: "subscription-plans",
    pagination: { pageSize: 100, mode: "server" },
    filters: [{ field: "isActive", operator: "eq", value: true }],
  });

  const booksQuery = useList<Book>({
    resource: "books",
    pagination: { pageSize: 100, mode: "server" },
  });

  const livestreamsQuery = useList<Livestream>({
    resource: "livestreams",
    pagination: { pageSize: 100, mode: "server" },
  });

  const tiersQuery = useCustom<PricingTier[]>({
    url: `${apiUrl}/pricing-tiers/admin`,
    method: "get",
    config: {
      query: {
        isActive: true,
        courseId: targetType === "COURSE" && targetId ? Number(targetId) : undefined,
        channelPassId: targetType === "CHANNEL_PASS" && targetId ? Number(targetId) : undefined,
        subscriptionPlanId: targetType === "SUBSCRIPTION_PLAN" && targetId ? Number(targetId) : undefined,
        bookId: targetType === "BOOK" && targetId ? Number(targetId) : undefined,
      },
    },
    queryOptions: {
      enabled: Boolean(targetSupportsPricingTier && targetType && targetId),
    },
  });

  const grantMutation = useCustomMutation();
  const revokeMutation = useCustomMutation();
  const updateMutation = useCustomMutation();

  const [editGrant, setEditGrant] = useState<AccessGrant | null>(null);
  const [editEndsAt, setEditEndsAt] = useState<Date | null>(null);

  const isLoading = grantsQuery.query?.isLoading ?? false;
  const refetch = grantsQuery.query?.refetch;
  const grants = grantsQuery.query?.data?.data ?? [];
  const courses = coursesQuery.result?.data ?? [];
  const channelPasses = channelPassesQuery.result?.data ?? [];
  const subscriptionPlans = subscriptionPlansQuery.result?.data ?? [];
  const books = booksQuery.result?.data ?? [];
  const livestreams = livestreamsQuery.result?.data ?? [];
  const pricingTiers = useMemo(() => {
    const raw = tiersQuery.query?.data?.data;
    return Array.isArray(raw) ? raw : [];
  }, [tiersQuery.query?.data?.data]);
  const tiersLoading = tiersQuery.query?.isLoading ?? false;
  const pricingTierPlaceholder = tiersLoading
    ? "Загрузка тарифов..."
    : (pricingTiers.length > 0 ? "Выберите тариф" : "Для выбранного объекта тарифов нет");

  const handleOpenGrantModal = () => {
    setTargetType("");
    setTargetId("");
    setPricingTierId("");
    setDurationDays("");
    setGrantModalOpen(true);
  };

  const handleGrantAccess = () => {
    grantMutation.mutate(
      {
        url: `/users/${userId}/access-grants`,
        method: "post",
        values: {
          targetType,
          targetId: targetId ? Number(targetId) : undefined,
          pricingTierId: pricingTierId ? Number(pricingTierId) : undefined,
          durationDays: durationDays ? Number(durationDays) : undefined,
        },
      },
      {
        onSuccess: () => {
          setGrantModalOpen(false);
          refetch?.();
          onGrantAccess?.();
          notify.success("Доступ выдан");
        },
        onError: () => {
          notify.error("Ошибка при выдаче доступа");
        },
      },
    );
  };

  const handleRevokeAccess = () => {
    if (deleteItems.length === 0) {
      return;
    }

    const promises = deleteItems.map(
      (grant) =>
        new Promise<void>((resolve) => {
          revokeMutation.mutate(
            {
              url: `/users/${userId}/access-grants/${grant.id}`,
              method: "delete",
              values: {},
            },
            {
              onSuccess: () => resolve(),
              onError: () => resolve(),
            },
          );
        }),
    );

    Promise.all(promises).then(() => {
      setDeleteItems([]);
      refetch?.();
      notify.success(
        deleteItems.length === 1 ? "Доступ отозван" : `Отозвано ${deleteItems.length} доступов`,
      );
    });
  };

  const handleOpenEdit = (grant: AccessGrant) => {
    setEditGrant(grant);
    // endsAt → Date для DatePicker по UTC-дню (без TZ-сдвига); бессрочный → null
    setEditEndsAt(grant.endsAt ? dateFromUtcDay(grant.endsAt) : null);
  };

  const handleSaveEndsAt = () => {
    if (!editGrant) {
      return;
    }
    updateMutation.mutate(
      {
        url: `/users/${userId}/access-grants/${editGrant.id}`,
        method: "patch",
        // дата без времени → конец выбранного дня UTC, чтобы доступ держался весь день
        values: { endsAt: editEndsAt ? endOfDayUtcIso(editEndsAt) : null },
      },
      {
        onSuccess: () => {
          setEditGrant(null);
          refetch?.();
          notify.success("Срок действия обновлён");
        },
        onError: () => {
          notify.error("Ошибка при обновлении срока");
        },
      },
    );
  };

  const getTargetIcon = (type: string) => {
    switch (type) {
      case "COURSE":
        return <BookOpen className="h-4 w-4" />;
      case "CHANNEL":
      case "CHANNEL_PASS":
        return <MessageSquare className="h-4 w-4" />;
      default:
        return <Shield className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (status: string, endsAt: string | null) => {
    const isExpired = endsAt && new Date(endsAt) < new Date();

    if (status === "REVOKED") {
      return (
        <Badge variant="destructive" className="gap-1">
          <XCircle className="h-3 w-3" />
          Отозван
        </Badge>
      );
    }

    if (isExpired) {
      return (
        <Badge variant="secondary" className="gap-1">
          <AlertCircle className="h-3 w-3" />
          Истёк
        </Badge>
      );
    }

    if (status === "ACTIVE") {
      return (
        <Badge className="bg-green-500 gap-1">
          <CheckCircle2 className="h-3 w-3" />
          Активен
        </Badge>
      );
    }

    return <Badge variant="outline">{status}</Badge>;
  };

  const getAvailableTargets = () => {
    switch (targetType) {
      case "COURSE":
        return courses.map((course) => ({
          id: Number(course.id),
          name: getCourseTitle(course),
        }));
      case "CHANNEL_PASS":
        return channelPasses.map((pass) => ({
          id: pass.id,
          name: getChannelPassName(pass),
        }));
      case "SUBSCRIPTION_PLAN":
        return subscriptionPlans.map((plan) => ({
          id: plan.id,
          name: getSubscriptionPlanName(plan),
        }));
      case "BOOK":
        return books.map((book) => ({
          id: book.id,
          name: getBookTitle(book),
        }));
      case "LIVESTREAM":
        return livestreams.map((livestream) => ({
          id: livestream.id,
          name: getLivestreamTitle(livestream).title,
        }));
      default:
        return [];
    }
  };

  const showInternalButton = !onGrantModalOpenChange;
  const targetRequiresTier = targetSupportsPricingTier && targetId !== "" && pricingTiers.length > 0;

  const columns: Column<AccessGrant>[] = [
    {
      key: "targetType",
      header: "Тип",
      render: (_, record) => (
        <div className="flex items-center gap-2">
          {getTargetIcon(record.targetType)}
          <span className="text-sm">{translateTargetType(record.targetType)}</span>
        </div>
      ),
    },
    {
      key: "targetName",
      header: "Объект",
      render: (value) => <span className="font-medium">{String(value)}</span>,
    },
    {
      key: "sourceType",
      header: "Источник",
      render: (_, record) => (
        <div>
          <Badge variant="outline" className="text-xs">
            {translateSourceType(record.sourceType)}
          </Badge>
          <div className="mt-1 text-xs text-muted-foreground">
            {getSourceDetails(record)}
          </div>
        </div>
      ),
    },
    {
      key: "status",
      header: "Статус",
      render: (_, record) => getStatusBadge(record.status, record.endsAt),
    },
    {
      key: "endsAt",
      header: "Срок действия",
      render: (value) => (
        <div className="text-sm">
          {value ? (
            <div className="flex items-center gap-1 text-muted-foreground">
              <Clock className="h-3 w-3" />
              до {formatDate(String(value))}
            </div>
          ) : (
            <span className="text-muted-foreground">Бессрочно</span>
          )}
        </div>
      ),
    },
    {
      key: "id",
      header: "",
      render: (_, record) => (
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => { e.stopPropagation(); handleOpenEdit(record); }}
            title="Изменить срок действия"
          >
            <Pencil className="h-3 w-3" />
          </Button>
        </div>
      ),
    },
  ];

  const bulkActions: BulkAction<AccessGrant>[] = [
    {
      label: "Отозвать доступ",
      icon: <Trash2 className="h-4 w-4" />,
      onClick: (items) => setDeleteItems(items.filter((item) => item.status === "ACTIVE")),
      variant: "destructive",
      showOnlyWhenSelected: true,
    },
  ];

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {showInternalButton && (
          <div className="flex justify-end">
            <Button onClick={handleOpenGrantModal}>
              <Plus className="mr-2 h-4 w-4" />
              Дать доступ
            </Button>
          </div>
        )}

        {grants.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Shield className="mx-auto mb-2 h-12 w-12 opacity-50" />
            <p>Нет выданных доступов</p>
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={grants}
            total={grants.length}
            page={1}
            pageSize={100}
            onPageChange={() => {}}
            emptyMessage="Нет выданных доступов"
            selectable
            bulkActions={bulkActions}
          />
        )}
      </div>

      <Dialog open={grantModalOpen} onOpenChange={setGrantModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Выдать доступ</DialogTitle>
            <DialogDescription>
              Выберите тип доступа и объект. Для продуктов с тарифами срок берётся из тарифа или настроек продукта.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Тип доступа</Label>
              <Select
                value={targetType}
                onValueChange={(value) => {
                  if (!isGrantTargetType(value)) {
                    return;
                  }
                  setTargetType(value);
                  setTargetId("");
                  setPricingTierId("");
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Выберите тип" />
                </SelectTrigger>
                <SelectContent>
                  {TARGET_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon className="h-4 w-4" />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {targetType && (
              <div className="space-y-2">
                <Label>Объект</Label>
                <Select
                  value={targetId}
                  onValueChange={(value) => {
                    setTargetId(value);
                    setPricingTierId("");
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите объект" />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableTargets().map((target) => (
                      <SelectItem key={target.id} value={String(target.id)}>
                        {target.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {targetId && targetSupportsPricingTier && (
              <div className="space-y-2">
                <Label>Тариф</Label>
                <Select
                  value={pricingTierId}
                  onValueChange={setPricingTierId}
                  disabled={tiersLoading || pricingTiers.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={pricingTierPlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {pricingTiers.map((tier) => (
                      <SelectItem key={tier.id} value={String(tier.id)}>
                        {getTierName(tier)} · {getTierPriceLabel(tier)}
                        {tier.durationDays ? ` · ${tier.durationDays} дн.` : " · бессрочно"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {pricingTiers.length > 0
                    ? "Для объектов с тарифами выбор тарифа обязателен."
                    : "Если тарифов нет, доступ будет выдан по базовым настройкам объекта."}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Срок действия (дней)</Label>
              <Input
                type="number"
                placeholder="Оставьте пустым, чтобы взять срок из тарифа или настроек объекта"
                value={durationDays}
                onChange={(event) => setDurationDays(event.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setGrantModalOpen(false)}>
              Отмена
            </Button>
            <Button
              onClick={handleGrantAccess}
              disabled={!targetType || !targetId || (targetRequiresTier && !pricingTierId) || grantMutation.mutation.isPending}
            >
              {grantMutation.mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Выдать доступ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editGrant !== null} onOpenChange={(open) => !open && setEditGrant(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Изменить срок действия</DialogTitle>
            <DialogDescription>
              {editGrant ? `Доступ: ${editGrant.targetName}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Действует до</Label>
              <DatePicker
                value={editEndsAt}
                onChange={setEditEndsAt}
                placeholder="Бессрочный доступ"
              />
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  Оставьте пустым, чтобы сделать доступ бессрочным.
                </p>
                {editEndsAt && (
                  <Button variant="ghost" size="sm" onClick={() => setEditEndsAt(null)}>
                    Сделать бессрочным
                  </Button>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditGrant(null)}>
              Отмена
            </Button>
            <Button onClick={handleSaveEndsAt} disabled={updateMutation.mutation.isPending}>
              {updateMutation.mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteItems.length > 0} onOpenChange={() => setDeleteItems([])}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteItems.length === 1 ? "Отозвать доступ?" : `Отозвать ${deleteItems.length} доступов?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteItems.length === 1
                ? `Вы уверены, что хотите отозвать доступ к "${deleteItems[0]?.targetName}"?`
                : "Вы уверены, что хотите отозвать выбранные доступы?"}{" "}
              Это действие нельзя отменить.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevokeAccess}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {revokeMutation.mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Отозвать
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
